import React, { useState } from "react";
import { Button } from "./ui/button";
import { MessageCircle, Phone, Mail, MapPin, Clock, Star, Shield, CheckCircle, Send } from "lucide-react";
import { useAnalytics } from "../hooks/useAnalytics";

const KontaktSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { trackFormSubmission, trackWhatsAppClick } = useAnalytics();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Track form submission
    trackFormSubmission('contact_form');
    
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
    setFormData({ name: "", email: "", message: "" });
  };

  const handleWhatsAppContact = () => {
    // Track WhatsApp contact (includes Google Ads conversion)
    trackWhatsAppClick('contact_section');
    
    const message = `Hallo und herzlich willkommen bei Floria Maler & Sanierung! 🎉😊

Wir freuen uns riesig, dass du bei uns bist! 🤩

Was können wir Schönes für dich tun? 🤔

Planst du frische Farben für dein Zuhause oder deine Geschäftsräume? 🏠🏢🎨
Möchtest du mehr über unsere kreativen Maltechniken und hochwertigen Materialien erfahren? ✨
Oder suchst du einfach einen zuverlässigen Partner für deine Malerarbeiten? 🤝

Erzähl uns, was du im Sinn hast, und wir melden uns so schnell wie möglich bei dir! 🚀

Vielen Dank für dein Interesse – wir freuen uns darauf, deine Wünsche zu erfüllen! 😊

Liebe Grüße,
Dein Team von Floria Maler & Sanierung 💐`;

    const whatsappUrl = `https://wa.me/4915758183212?text=${encodeURIComponent(message)}`;
    // Use window.open for reliability - this always works even if tracking fails
    window.open(whatsappUrl, '_blank');
  };

  const handlePhoneCall = (number) => {
    window.location.href = `tel:${number}`;
  };

  return (
    <section id="kontakt" className="py-8 md:py-20 bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-green-600 rounded-full mb-6 shadow-2xl">
            <MessageCircle className="text-white" size={40} />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Kontakt zu <span className="text-blue-600">Floria</span>{" "}
            <span className="text-green-600">Maler & Sanierung</span>
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 mb-4 max-w-4xl mx-auto leading-relaxed">
            🎨 Wir sind für Sie da - Schnell, zuverlässig und professionell
          </p>
          <p className="text-base sm:text-lg text-green-600 font-semibold">
            Antwort per WhatsApp!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Information */}
          <div className="space-y-6 lg:space-y-8 order-2 lg:order-1">
            {/* WhatsApp CTA */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-3xl p-8 text-white shadow-2xl">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
                  <MessageCircle className="text-green-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold mb-2">Kontaktiere uns jetzt über WhatsApp!</h2>
                <p className="text-green-100">Schnellste und einfachste Art uns zu erreichen</p>
              </div>
              <Button 
                onClick={handleWhatsAppContact}
                className="w-full bg-white text-green-600 hover:bg-green-50 px-8 py-6 text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="mr-3" size={28} />
                WhatsApp Chat starten
              </Button>
            </div>

            {/* Company Information */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Floria Maler & Sanierung</h3>
              
              {/* Main Address */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Hauptsitz</h4>
                    <p className="text-gray-700">
                      Aixheimerstrasse 50/4<br />
                      78554 Aldingen<br />
                      Baden-Württemberg
                    </p>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Telefon</h4>
                    <div className="space-y-1">
                      <button 
                        onClick={() => handlePhoneCall('+4915901978718')}
                        className="block text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                      >
                        0159.01978718
                      </button>
                      <button 
                        onClick={() => handlePhoneCall('+4915758183212')}
                        className="block text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                      >
                        0157.58183212
                      </button>
                    </div>
                  </div>
                </div>

                {/* USt-ID */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Umsatzsteuer-ID</h4>
                    <p className="text-gray-700">DE319110250</p>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Erreichbarkeit</h4>
                    <div className="text-gray-700 space-y-1">
                      <p>Termine nach Vereinbarung</p>
                      <p>WhatsApp: Jederzeit erreichbar</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Virtual Offices */}
            <div className="bg-blue-50 rounded-3xl p-8 border-2 border-blue-200">
              <h3 className="text-xl font-bold text-blue-900 mb-4">Virtuelle Büros</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-blue-800">Freiburg</h4>
                  <p className="text-blue-700">Kaiser Joseph Strasse, 79098 Freiburg</p>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800">Stuttgart</h4>
                  <p className="text-blue-700">Lautenschlagerstraße 23, 70173 Stuttgart</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form & Additional Info */}
          <div className="space-y-6 lg:space-y-8 order-1 lg:order-2">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Nachricht senden</h3>
              
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-green-600 mb-2">Nachricht erhalten!</h4>
                  <p className="text-gray-600">Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ihr vollständiger Name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="ihre.email@beispiel.de"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ihre Anfrage *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Beschreiben Sie kurz Ihr Malerprojekt..."
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-8 py-4 text-lg font-bold rounded-xl shadow-lg transition-all duration-300"
                  >
                    <Send className="mr-3" size={20} />
                    Nachricht senden
                  </Button>
                </form>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6 text-center">Warum Floria Maler wählen?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <Star className="text-yellow-300" size={24} />
                  <span>Erfahrung seit 2018</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="text-yellow-300" size={24} />
                  <span>Vollversichert & Zertifiziert</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-yellow-300" size={24} />
                  <span>Festpreisgarantie</span>
                </div>
                <div className="flex items-center gap-3">
                  <MessageCircle className="text-yellow-300" size={24} />
                  <span>Schnelle Rückmeldung</span>
                </div>
              </div>
            </div>

            {/* Website Links */}
            <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Unsere Webseiten</h3>
              <div className="grid grid-cols-2 gap-4">
                <a href="https://www.malerstuttgart.com" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                  malerstuttgart.com
                </a>
                <a href="https://www.malerfreiburg.de" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                  malerfreiburg.de
                </a>
                <a href="https://www.malertuebingen.de" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                  malertuebingen.de
                </a>
                <a href="https://www.malerkonstanz.de" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                  malerkonstanz.de
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Map Placeholder */}
        <div className="mt-16">
          <div className="bg-gray-200 rounded-3xl h-96 flex items-center justify-center border-2 border-gray-300">
            <div className="text-center">
              <MapPin className="mx-auto text-gray-500 mb-4" size={48} />
              <h3 className="text-xl font-bold text-gray-700 mb-2">Interaktive Karte</h3>
              <p className="text-gray-600">
                Aixheimerstrasse 50/4, 78554 Aldingen, Baden-Württemberg
              </p>
              <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                Route planen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KontaktSection;